import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BRAND } from '../config/branding';
import './CertificateVerification.css';

/**
 * Public Certificate Verification Page
 *
 * Allows anyone to verify a certificate issued by ICSC BOSS without login.
 * Displays certificate metadata and validity status.
 *
 * Used by:
 * - ICSC Website (Council for Standards)
 * - Certificate holders who want to verify their certificates
 * - Third parties who want to verify certificate authenticity
 */
function CertificateVerification() {
  const { certificateId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCertificateVerification();
  }, [certificateId]);

  const fetchCertificateVerification = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/public/verify-certificate/${certificateId}`
      );

      if (!response.ok) {
        throw new Error('Failed to verify certificate');
      }

      const data = await response.json();
      setCertificate(data);
    } catch (err) {
      console.error('Error verifying certificate:', err);
      setError('Unable to verify certificate. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!certificate) return null;

    const statusConfig = {
      valid: {
        icon: '✓',
        className: 'status-valid',
        label: 'VALID CERTIFICATE'
      },
      revoked: {
        icon: '✗',
        className: 'status-revoked',
        label: 'REVOKED'
      },
      expired: {
        icon: '⚠',
        className: 'status-expired',
        label: 'EXPIRED'
      },
      not_found: {
        icon: '✗',
        className: 'status-invalid',
        label: 'INVALID'
      },
      invalid: {
        icon: '✗',
        className: 'status-invalid',
        label: 'INVALID'
      }
    };

    const config = statusConfig[certificate.status] || statusConfig.invalid;

    return (
      <div className={`status-badge ${config.className}`}>
        <span className="status-icon">{config.icon}</span>
        <span className="status-label">{config.label}</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="verification-container">
        <div className="verification-card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Verifying certificate...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="verification-container">
        <div className="verification-card">
          <div className="error-state">
            <div className="error-icon">✗</div>
            <h2>Verification Error</h2>
            <p>{error}</p>
            <button onClick={fetchCertificateVerification} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-container">
      <div className="verification-header">
        <h1>{BRAND.name}</h1>
        <p className="subtitle">Certificate Verification System</p>
      </div>

      <div className="verification-card">
        {/* Status Badge */}
        {getStatusBadge()}

        {/* Certificate Information */}
        <div className="certificate-info">
          {certificate.valid ? (
            <>
              <h2 className="certificate-title">
                This certificate is valid and authentic
              </h2>
              <p className="certificate-description">
                This certificate has been issued by {BRAND.short} and is currently active.
              </p>
            </>
          ) : (
            <>
              <h2 className="certificate-title error-title">
                Certificate Verification Failed
              </h2>
              <p className="certificate-description error-description">
                {certificate.reason || 'This certificate is not valid.'}
              </p>
            </>
          )}

          {/* Certificate Details */}
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Certificate Type</span>
              <span className="detail-value">{certificate.certificate_type}</span>
            </div>

            {certificate.certificate_number && (
              <div className="detail-item">
                <span className="detail-label">Certificate Number</span>
                <span className="detail-value">{certificate.certificate_number}</span>
              </div>
            )}

            {certificate.issued_to && (
              <div className="detail-item">
                <span className="detail-label">Issued To</span>
                <span className="detail-value">{certificate.issued_to}</span>
              </div>
            )}

            {certificate.issued_by && (
              <div className="detail-item">
                <span className="detail-label">Issued By</span>
                <span className="detail-value">{certificate.issued_by}</span>
              </div>
            )}

            {certificate.issue_date && (
              <div className="detail-item">
                <span className="detail-label">Issue Date</span>
                <span className="detail-value">{formatDate(certificate.issue_date)}</span>
              </div>
            )}

            {certificate.expiry_date && (
              <div className="detail-item">
                <span className="detail-label">Expiry Date</span>
                <span className="detail-value">{formatDate(certificate.expiry_date)}</span>
              </div>
            )}

            <div className="detail-item">
              <span className="detail-label">Certificate ID</span>
              <span className="detail-value certificate-id">{certificate.certificate_id}</span>
            </div>

            <div className="detail-item">
              <span className="detail-label">Verification Time</span>
              <span className="detail-value">
                {formatDate(certificate.verification_timestamp)}
              </span>
            </div>
          </div>

          {/* Security Notice */}
          <div className="security-notice">
            <div className="notice-icon">🔒</div>
            <div className="notice-content">
              <p className="notice-title">Security Notice</p>
              <p className="notice-text">
                This verification was performed in real-time against the {BRAND.short} certification database.
                For questions about this certificate, please contact the issuing certification body.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button onClick={fetchCertificateVerification} className="button-secondary">
              Verify Again
            </button>
            <button onClick={() => window.print()} className="button-primary">
              Print Verification
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="verification-footer">
        <p>
          Powered by {BRAND.name} - International Council for Standards & Certification
        </p>
        <p className="footer-links">
          <a href="/privacy">Privacy Policy</a>
          {' · '}
          <a href="/terms">Terms of Service</a>
        </p>
      </div>
    </div>
  );
}

export default CertificateVerification;
